import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { api, friendlyError } from '../lib/api-client';
import { formatDate } from '../i18n/formatters';
import {
  PartnerReferralsResponse,
  referralSourceLabel,
  referralStatusBadgeVariant,
  referralStatusLabel,
} from '../lib/partner-portal';
import Container from '../components/ui/Container';
import PageHeader from '../components/ui/PageHeader';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import AlertBanner from '../components/ui/AlertBanner';
import EmptyState from '../components/ui/EmptyState';
import {
  PartnerHeaderMeta,
  PartnerPagination,
  portalTableCellStyle,
  portalTableHeaderStyle,
} from '../components/partner-portal/shared';

export default function PartnerReferralsPage() {
  const { t } = useTranslation(['partnerPortal', 'common']);
  const { partnerAccess } = useAuth();
  const [page, setPage] = useState(1);
  const [data, setData] = useState<PartnerReferralsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await api.get<PartnerReferralsResponse>(`/partner/referrals?page=${page}&pageSize=25`);
        setData(response);
      } catch (err) {
        setError(friendlyError(err, t('errors.load')));
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [page, t]);

  return (
    <Container maxWidth={1320}>
      <div style={{ display: 'grid', gap: 20 }}>
        <PageHeader
          eyebrow={t('referrals.eyebrow')}
          title={t('referrals.title')}
          subtitle={partnerAccess?.partnerName}
          description={t('referrals.description')}
          meta={partnerAccess ? <PartnerHeaderMeta access={partnerAccess} /> : undefined}
        />

        {error && <AlertBanner variant="danger">{error}</AlertBanner>}

        <Card>
          {loading ? (
            <div>{t('common:actions.loading')}</div>
          ) : !data || data.referrals.length === 0 ? (
            <EmptyState
              title={t('empty.referralsTitle')}
              description={t('empty.referralsDescription')}
              dashed
            />
          ) : (
            <div style={{ display: 'grid', gap: 16 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={portalTableHeaderStyle}>{t('fields.company')}</th>
                    <th style={portalTableHeaderStyle}>{t('fields.organisation')}</th>
                    <th style={portalTableHeaderStyle}>{t('fields.source')}</th>
                    <th style={portalTableHeaderStyle}>{t('fields.status')}</th>
                    <th style={portalTableHeaderStyle}>{t('fields.attributed')}</th>
                    <th style={portalTableHeaderStyle}>{t('fields.converted')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.referrals.map((referral) => (
                    <tr key={referral.id}>
                      <td style={portalTableCellStyle}>
                        <div style={{ fontWeight: 700 }}>
                          {referral.prospectCompany ?? referral.prospectName ?? t('common:labels.notAvailable')}
                        </div>
                        <div style={{ color: 'var(--acc-color-text-muted)', marginTop: 4 }}>
                          {referral.prospectEmail ?? t('common:labels.notAvailable')}
                        </div>
                      </td>
                      <td style={portalTableCellStyle}>
                        <div>{referral.organisationName ?? t('common:labels.notAvailable')}</div>
                        <div style={{ color: 'var(--acc-color-text-muted)', marginTop: 4 }}>
                          {referral.organisationSlug ?? ''}
                        </div>
                      </td>
                      <td style={portalTableCellStyle}>{referralSourceLabel(referral.sourceType, t)}</td>
                      <td style={portalTableCellStyle}>
                        <Badge variant={referralStatusBadgeVariant(referral.status)}>
                          {referralStatusLabel(referral.status, t)}
                        </Badge>
                      </td>
                      <td style={portalTableCellStyle}>{formatDate(referral.attributedAt) || t('common:labels.notAvailable')}</td>
                      <td style={portalTableCellStyle}>{formatDate(referral.convertedAt) || t('common:labels.notAvailable')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <PartnerPagination
                page={data.page}
                pageSize={data.pageSize}
                total={data.total}
                onPageChange={setPage}
              />
            </div>
          )}
        </Card>
      </div>
    </Container>
  );
}
